import {BrowserModule} from '@angular/platform-browser';
import {APP_INITIALIZER, ErrorHandler, NgModule} from '@angular/core';

import {AppComponent} from './app.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ServiceWorkerModule} from '@angular/service-worker';
import {environment} from '../environments/environment';
import {AppRoutingModule} from './app-routing.module';
import {MatToolbarModule} from '@angular/material/toolbar';
import {DateAdapter, MAT_DATE_LOCALE} from '@angular/material/core';
import {MatButtonModule} from '@angular/material/button';
import {CustomDateAdapter} from './shared/custom-date-adapter';
import {HomeComponent} from './pages/home/home.component';
import {BaseComponent} from './pages/base/base.component';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {AngularFireModule} from '@angular/fire/compat';
import {AngularFireAuthModule} from '@angular/fire/compat/auth';
import {LoggerModule, NgxLoggerLevel} from 'ngx-logger';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MAT_RADIO_DEFAULT_OPTIONS} from '@angular/material/radio';
import {InformationsComponent} from './pages/informations/informations.component';
import {AuthInterceptor} from './services/auth-interceptor.service';
import {SharedModule} from './shared/shared.module';
import {registerLocaleData} from '@angular/common';
import localePt from '@angular/common/locales/pt';
import {SickNoteModule} from './pages/sick-note/sick-note.module';
import {ChatModule} from './pages/chat/chat.module';
import {LoginModule} from './pages/login/login.module';
import {PersonalDataModule} from './pages/validation/validation.module';
import {LoadingModule} from './components/loading/loading.module';
import {EditModule} from './components/edit/edit.module';
import {SimpleDialogModule} from './components/simple-dialog/simple-dialog.module';
import {SpinnerModule} from './components/spinner/spinner.module';
import * as Sentry from '@sentry/angular';
import {Router} from '@angular/router';
import {IntercomModule} from 'ng-intercom';
import {SentryErrorHandler} from './shared/sentry-error-handler';
import {AngularFireAnalyticsModule, APP_NAME, APP_VERSION, ScreenTrackingService, UserTrackingService} from '@angular/fire/compat/analytics';
import {ItemPickerModule} from './components/item-picker/item-picker.module';
import {AngularFirePerformanceModule} from '@angular/fire/compat/performance';
import {AngularFireRemoteConfigModule, DEFAULTS, SETTINGS as RC_SETTINGS} from '@angular/fire/compat/remote-config';
import {FeatureFlagsService} from './services/feature-flags.service';
import {TermsModule} from './pages/terms/terms.module';
import {DocTypeModule} from './pages/doc-type/doc-type.module';
import {ClipboardModule} from '@angular/cdk/clipboard';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatDividerModule} from '@angular/material/divider';
import {MatListModule} from '@angular/material/list';

registerLocaleData(localePt, 'pt-BR');

const LOG_LEVEL: NgxLoggerLevel = environment.production ? NgxLoggerLevel.WARN : NgxLoggerLevel.DEBUG;

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    BaseComponent,
    InformationsComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    ServiceWorkerModule.register('ngsw-worker.js', {enabled: true}),
    AppRoutingModule,
    MatToolbarModule,
    MatButtonModule,
    HttpClientModule,
    LoggerModule.forRoot({level: LOG_LEVEL, serverLogLevel: NgxLoggerLevel.OFF}),
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireAuthModule,
    AngularFireAnalyticsModule,
    AngularFirePerformanceModule,
    AngularFireRemoteConfigModule,
    MatIconModule,
    MatProgressSpinnerModule,
    SickNoteModule,
    ChatModule,
    LoginModule,
    PersonalDataModule,
    LoadingModule,
    EditModule,
    SimpleDialogModule,
    ItemPickerModule,
    SpinnerModule,
    IntercomModule.forRoot({
      appId: environment.intercomAppId,
      updateOnRouterChange: true // will automatically run `update` on router event changes. Default: `false`
    }),
    TermsModule,
    DocTypeModule,
    ClipboardModule,
    MatSnackBarModule,
    MatSidenavModule,
    MatDividerModule,
    MatListModule
  ],
  providers: [
    ScreenTrackingService,
    UserTrackingService,
    {provide: APP_NAME, useValue: environment.firebase.projectId},
    {provide: APP_VERSION, useValue: environment.version},
    {provide: MAT_DATE_LOCALE, useValue: 'pt-BR'},
    {provide: DateAdapter, useClass: CustomDateAdapter},
    {provide: ErrorHandler, useClass: SentryErrorHandler},
    {provide: Sentry.TraceService, deps: [Router]},
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
      },
      deps: [Sentry.TraceService],
      multi: true,
    },
    {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
    {provide: MAT_RADIO_DEFAULT_OPTIONS, useValue: {color: 'primary'}},
    {provide: DEFAULTS, useValue: {terms_warning: ''}},
    {
      provide: RC_SETTINGS,
      useFactory: () =>
        environment.production ? {} : {minimumFetchIntervalMillis: 10_000},
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (featureFlags: FeatureFlagsService) => () => featureFlags.load(),
      deps: [FeatureFlagsService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
